import { create } from 'zustand';

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;             // default: 30 min
const ADMIN_INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;  // super_admin: 12 hours

// super_admin (the platform owner's account) gets a much longer idle window so it
// doesn't get force-logged-out after a short break. Others keep the 30-min default.
function inactivityLimit(): number {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    if (u && u.role === 'super_admin') return ADMIN_INACTIVITY_TIMEOUT_MS;
  } catch { /* ignore parse errors */ }
  return INACTIVITY_TIMEOUT_MS;
}

interface User {
  id: string;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  tenant_id: string;
  mfa_enabled?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  setAuth: (user: User, token: string) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  checkSession: () => boolean;
  hasRole: (roles: string[]) => boolean;
}

// Linear hierarchy for legacy roles
const ROLE_HIERARCHY = ['readonly', 'customer_user', 'customer_admin', 'reseller_admin', 'provider_admin', 'super_admin'];

// Non-hierarchical roles — must be explicitly listed to be granted
export const FLAT_ROLES = new Set(['technical_admin', 'billing_admin', 'executive', 'l2_engineer']);

// Display labels for all roles
// Sees everything a super admin does, but all money/cost figures are masked
// server-side (FinancialMaskInterceptor). Registered here for labels + access.
// Roles whose money/cost figures are hidden in the UI (menus, dashboard cards,
// balance columns). sub_super_admin = all money masked; ops_admin = buy/profit
// masked but sell rates kept (server enforces via FinancialMaskInterceptor).
// l2_engineer = monitoring-only engineer: no money anywhere, read-only (server allowlist).
export const financialHidden = (role?: string) => role === 'sub_super_admin' || role === 'ops_admin' || role === 'l2_engineer';

// Pages an L2 Engineer may open (server enforces the matching API allowlist in l2-policy.ts).
export const L2_PATHS = new Set(['/dashboard', '/dashboard/health', '/dashboard/health/security', '/calls/live', '/monitoring/failures', '/cdr', '/calls/logs',
  '/sbc', '/sbc-control', '/acl', '/sip-devices', '/tickets', '/security']);

export const ROLE_LABELS: Record<string, string> = {
  super_admin:      'Super Admin',
  sub_super_admin:  'Sub Super Admin',
  ops_admin:        'Operations Admin',
  technical_admin:  'Technical Admin',
  l2_engineer:      'L2 Engineer',
  executive:         'Executive',
  billing_admin:    'Billing Admin',
  provider_admin:   'Provider Admin',
  reseller_admin:   'Reseller Admin',
  customer_admin:   'Customer Admin',
  customer_user:    'Customer User',
  readonly:         'Read Only',
};

// MUI chip colours per role
export const ROLE_COLORS: Record<string, 'error' | 'secondary' | 'warning' | 'info' | 'primary' | 'default'> = {
  super_admin:      'error',
  sub_super_admin:  'warning',
  ops_admin:        'secondary',
  technical_admin:  'secondary',
  l2_engineer:      'info',
  executive:         'info',
  billing_admin:    'warning',
  provider_admin:   'error',
  reseller_admin:   'info',
  customer_admin:   'primary',
  customer_user:    'default',
  readonly:         'default',
};

function parseJwtExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isSessionValid(token: string): boolean {
  const expiry = parseJwtExpiry(token);
  if (expiry && Date.now() > expiry) return false;
  const lastActivityStr = localStorage.getItem('last_activity');
  if (!lastActivityStr) return false;
  if (Date.now() - parseInt(lastActivityStr) > inactivityLimit()) return false;
  return true;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: JSON.parse(localStorage.getItem('user') || 'null'),
  token: localStorage.getItem('access_token'),

  setAuth: (user, token) => {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('access_token', token);
    localStorage.setItem('last_activity', Date.now().toString());
    set({ user, token });
  },

  logout: () => {
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    localStorage.removeItem('last_activity');
    set({ user: null, token: null });
  },

  isAuthenticated: () => {
    const { token, user } = get();
    if (!token || !user) return false;
    if (!isSessionValid(token)) { get().logout(); return false; }
    return true;
  },

  checkSession: () => {
    const { token, user } = get();
    if (!token || !user) return false;
    if (!isSessionValid(token)) { get().logout(); return false; }
    return true;
  },

  // roles: list of roles that are allowed.
  // super_admin always passes.
  // flat roles (technical_admin, billing_admin) must be explicitly in the list.
  // legacy roles use hierarchy: user level >= required level.
  hasRole: (roles: string[]) => {
    const user = get().user;
    if (!user) return false;
    if (user.role === 'super_admin' || user.role === 'sub_super_admin' || user.role === 'ops_admin') return true;
    if (FLAT_ROLES.has(user.role)) return roles.includes(user.role);
    const userLevel = ROLE_HIERARCHY.indexOf(user.role);
    return roles.some(r => {
      const reqLevel = ROLE_HIERARCHY.indexOf(r);
      return userLevel >= 0 && reqLevel >= 0 && userLevel >= reqLevel;
    });
  },
}));
